import * as zod from 'zod';

import { UnitQuaternion } from '@sb/geometry';

/**
 * Wire-contract types the UI supplies for trajectory-controller commands.
 * Botman composes the remaining wire fields (see `composeCartesianFrames`).
 * More online-planner command types belong here as they are wired up.
 */

/**
 * The frame choice behind a TC Cartesian primitive's `reference` and
 * `velocity_expressed_in` slots, given with the frame names the UI uses:
 * SpeedL's jog axes, and the frame a streamed ServoL pose is expressed in.
 * `parent` names the frame. `rotationInParent` rotates the axes inside that
 * frame. It is identity except plane align, which sends the plane
 * orientation with parent 'world'. The UI displays plane axes anchored to
 * the world.
 *
 * 'base' stays identity: the TC's BASE frame already carries the
 * custom-base placement (world_to_base). 'tcp' means the displayed
 * tooltip axes; the wire has no TCP built-in frame, so botman lowers
 * 'tcp' to FLANGE plus the flange-to-TCP rotation
 * (see `composeCartesianFrames`).
 */
export const VelocityExpressedIn = zod.object({
  parent: zod.enum(['world', 'base', 'tcp']),
  rotationInParent: UnitQuaternion,
});

export type VelocityExpressedIn = zod.infer<typeof VelocityExpressedIn>;

/**
 * The fields of a SpeedL command that the UI supplies. The UI cannot
 * fill the other SpeedL fields. `commanded_frame` needs the flange-to-TCP
 * transform, and only botman has that transform. `reference` follows from
 * `velocityExpressedIn.parent` (see `composeCartesianFrames`).
 *
 * `direction` has the shape of the wire CartesianDirection. It selects
 * the jog axis along the axes of `velocityExpressedIn`: one entry is +1
 * or -1, all other entries are zero, never pre-rotated. The shape allows
 * any per-axis values, so future streaming modes can carry velocities in
 * the same field. `validateSpeedLDirection` enforces today's rule: one
 * entry is -1 or +1, all others are 0.
 */
export const SpeedLJog = zod.object({
  direction: zod.object({
    linear: zod.tuple([zod.number(), zod.number(), zod.number()]),
    angular: zod.tuple([zod.number(), zod.number(), zod.number()]),
  }),
  velocityExpressedIn: VelocityExpressedIn,
});

export type SpeedLJog = zod.infer<typeof SpeedLJog>;
